// Client-side support-context proposal (typed help/intake framework, commit 2).
//
// Collects ONLY what the shared allowlist declares, and only from sources a
// reporter would expect a support form to read: the current route, the window
// size, the browser's own self-description, the time zone, and the online flag.
//
// Explicitly NOT collected — and there is no code path here that could:
//   - cookies, localStorage, sessionStorage, or the clipboard;
//   - request/response headers or any Authorization value;
//   - DOM text, form values, or the contents of any input;
//   - the full URL, its query string, or the hostname (only a route TEMPLATE);
//   - screenshots. A capture is attached only by an explicit, separate user
//     action, after the CUI warning — never automatically.
//
// Everything here is a PROPOSAL. The server re-validates every key, drops what it
// does not recognize, and overwrites the fields it owns (roles, auth mode, build,
// environment) regardless of what this function returned.

import { SUPPORT_CONTEXT_SCHEMA_VERSION } from '@dha-nexus/shared';

// The browser exposes a long, fingerprint-heavy user-agent string. Rather than
// forward it, this reduces it to a family plus a major version — enough for the
// service desk to reproduce, and nothing more.
export function browserIdentity(userAgent = '') {
  const ua = String(userAgent || '');
  const families = [
    ['Edge', /Edg\/(\d+)/],
    ['Chrome', /Chrome\/(\d+)/],
    ['Firefox', /Firefox\/(\d+)/],
    ['Safari', /Version\/(\d+).*Safari/],
  ];
  for (const [family, pattern] of families) {
    const match = pattern.exec(ua);
    if (match) return { browserFamily: family, browserVersion: match[1] };
  }
  return { browserFamily: null, browserVersion: null };
}

export function osIdentity(userAgent = '') {
  const ua = String(userAgent || '');
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/(iPhone|iPad|iPod)/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  return null;
}

// A route TEMPLATE, never the live URL. Concrete id segments are replaced with a
// placeholder so no entity id, and no query value, can ride along.
export function routeTemplateFromPathname(pathname = '/') {
  return (
    String(pathname || '/')
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .map((segment) => {
        if (!segment) return segment;
        // Anything that looks like a generated identifier becomes ':id'.
        if (/^[A-Z]{2,}-[A-Za-z0-9-]+$/.test(segment)) return ':id';
        if (/^\d+$/.test(segment)) return ':id';
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
          return ':id';
        }
        return segment;
      })
      .join('/') || '/'
  );
}

/**
 * Builds the proposed envelope.
 *
 * `pageTitle` comes from the shell's page metadata (a static, curated string),
 * NOT from document.title or any rendered content, so a page that happens to
 * display a record name cannot leak it here.
 */
export function collectSupportContext({
  pathname = '/',
  pageTitle = null,
  entityType = null,
  recentApiError = null,
  featureFlags = null,
  now = new Date(),
  win = typeof window === 'undefined' ? null : window,
} = {}) {
  const navigator = win?.navigator || null;
  const { browserFamily, browserVersion } = browserIdentity(navigator?.userAgent || '');
  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timezone = null;
  }

  const context = {
    // Stamped for readability; the server replaces it with its own value.
    schemaVersion: SUPPORT_CONTEXT_SCHEMA_VERSION,
    capturedAt: now.toISOString(),
    routeTemplate: routeTemplateFromPathname(pathname),
    pageTitle: pageTitle || null,
    entityType: entityType || null,
    browserFamily,
    browserVersion,
    osFamily: osIdentity(navigator?.userAgent || ''),
    viewportWidth: Number.isInteger(win?.innerWidth) ? win.innerWidth : null,
    viewportHeight: Number.isInteger(win?.innerHeight) ? win.innerHeight : null,
    timezone,
    online: typeof navigator?.onLine === 'boolean' ? navigator.onLine : null,
  };

  if (recentApiError) {
    // Rebuilt field by field. Even if a caller hands over a whole error object,
    // only these four values are read.
    context.recentApiError = {
      method: recentApiError.method || null,
      routeTemplate: recentApiError.routeTemplate || null,
      statusCode: Number.isInteger(recentApiError.statusCode) ? recentApiError.statusCode : null,
      requestId: recentApiError.requestId || null,
    };
  }
  if (featureFlags) context.featureFlags = featureFlags;

  // Null-valued keys are dropped rather than sent: an explicit null is not
  // information, and the server would only report it as invalid.
  return Object.fromEntries(Object.entries(context).filter(([, value]) => value != null));
}
